<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import type { QCard, QDialog } from 'quasar';
import { nativeApp } from '@/shared/services/native-app';

const BOTTOM_SHEET_CLOSE_THRESHOLD_VH = 8;
const BOTTOM_SHEET_SWIPE_CLOSE_VELOCITY = 0.75;
let openedBottomSheets = 0;

function syncSwipeRefreshState() {
  nativeApp.swipeRefreshEnabled(openedBottomSheets <= 0);
}

function registerOpenedBottomSheet() {
  openedBottomSheets += 1;
  syncSwipeRefreshState();
}

function unregisterOpenedBottomSheet() {
  openedBottomSheets = Math.max(0, openedBottomSheets - 1);
  syncSwipeRefreshState();
}

export default defineComponent({
  name: 'UiBottomSheet',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    middleOpening: {
      type: Boolean,
      default: false,
    },
    fullOpening: {
      type: Boolean,
      default: false,
    },
    seamless: {
      type: Boolean,
      default: false,
    },
    contentFullHeight: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    initialHeight: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
    minHeight: {
      type: Number,
      default: 25,
    },
    maxHeight: {
      type: Number,
      default: 100,
    },
    snap: {
      type: Boolean,
      default: true,
    },
    resizable: {
      type: Boolean,
      default: true,
    },
    showHandle: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    contentPadding: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'update:height'],
  setup(props, { emit, expose }) {
    const sheet = ref<QCard | null>(null);
    const dialog = ref<QDialog | null>(null);
    let dragPosition: number | null = null;
    let activePointerId: number | null = null;
    let dragVelocity = 0;
    let dragTimestamp = 0;
    const sheetHeight = ref<number | null>(null);
    const isDragging = ref(false);
    let initialSheetHeight = 0;
    let isSheetOpen = false;
    let viewportHeight = 0;

    const isInlineVisible = computed(() => props.modelValue !== false);
    const shouldShowHandle = computed(() => props.showHandle ?? !props.persistent);
    const sheetClasses = computed(() => ({
      'middle-opening': props.middleOpening,
      'bottom-sheet--full-opening': props.fullOpening,
      'bottom-sheet--inline': props.inline,
      'drag-moving': isDragging.value,
    }));
    const contentClasses = computed(() => ({
      'content_full-height': props.contentFullHeight,
    }));
    const contentStyle = computed(() => (props.contentPadding ? { padding: props.contentPadding } : undefined));

    const getSheetElement = () => sheet.value?.$el as HTMLElement | undefined;
    const clampSheetHeight = (value: number) => Math.max(props.minHeight, Math.min(props.maxHeight, value));
    const getViewportHeight = () =>
      window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0;
    const getResolvedViewportHeight = () => viewportHeight || getViewportHeight();
    const getHeightInVH = (heightInPX: number) => (heightInPX / getResolvedViewportHeight()) * 100;
    const getHeightInPX = (heightInVH: number) => (getResolvedViewportHeight() * heightInVH) / 100;
    const getCloseThreshold = () => Math.min(initialSheetHeight, props.minHeight + BOTTOM_SHEET_CLOSE_THRESHOLD_VH);
    const getCurrentSheetHeight = () => {
      if (sheetHeight.value !== null) return sheetHeight.value;

      const currentHeight = getSheetElement()?.offsetHeight || 0;
      return currentHeight ? getHeightInVH(currentHeight) : props.initialHeight || props.minHeight;
    };
    const getInitialSheetHeight = () =>
      clampSheetHeight(props.fullOpening ? props.maxHeight : (props.initialHeight ?? getCurrentSheetHeight()));
    const sheetStyle = computed(() => {
      const height = sheetHeight.value ?? (props.fullOpening ? props.maxHeight : undefined);

      return height === undefined ? undefined : { height: `${getHeightInPX(height)}px` };
    });
    const isEditableElementFocused = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) return false;
      if (activeElement.isContentEditable) return true;

      return ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName);
    };

    const setSheetOpened = (value: boolean) => {
      if (isSheetOpen === value) return;

      isSheetOpen = value;

      if (value) {
        registerOpenedBottomSheet();
        return;
      }

      unregisterOpenedBottomSheet();
    };

    const setSheetHeight = (value: number) => {
      sheetHeight.value = clampSheetHeight(value);
      emit('update:height', sheetHeight.value);
    };

    const syncSheetHeightWithViewport = () => {
      if (sheetHeight.value === null && !props.fullOpening) return;

      setSheetHeight(sheetHeight.value ?? props.maxHeight);
    };

    const handleViewportResize = () => {
      const nextViewportHeight = getViewportHeight();
      if (!nextViewportHeight) return;

      if (!viewportHeight) {
        viewportHeight = nextViewportHeight;
        return;
      }

      const isKeyboardResize = nextViewportHeight < viewportHeight && isEditableElementFocused();

      if (isKeyboardResize) {
        return;
      }

      viewportHeight = nextViewportHeight;
      syncSheetHeightWithViewport();
    };

    const setInitialSheetHeight = async () => {
      await nextTick();
      viewportHeight = getViewportHeight();

      initialSheetHeight = getInitialSheetHeight();
      setSheetHeight(initialSheetHeight);
    };

    const prepareSheetHeight = () => {
      viewportHeight = getViewportHeight();

      if (props.fullOpening || props.initialHeight !== undefined) {
        initialSheetHeight = getInitialSheetHeight();
        setSheetHeight(initialSheetHeight);
      }
    };

    const setDragMoving = (verdict: boolean) => {
      isDragging.value = verdict;
    };

    const onDragStart = (event: PointerEvent) => {
      if (!props.resizable) return;

      activePointerId = event.pointerId;
      dragPosition = event.pageY;
      dragVelocity = 0;
      dragTimestamp = event.timeStamp;
      setSheetHeight(getCurrentSheetHeight());
      (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
      setDragMoving(true);
      event.preventDefault();
    };

    const onDragMove = (event: PointerEvent) => {
      if (dragPosition === null || activePointerId !== event.pointerId) return;

      const deltaY = dragPosition - event.pageY;
      if (!deltaY) return;

      const deltaHeight = getHeightInVH(deltaY);
      const elapsed = Math.max(event.timeStamp - dragTimestamp, 1);
      dragVelocity = (event.pageY - dragPosition) / elapsed;
      dragPosition = event.pageY;
      dragTimestamp = event.timeStamp;
      setSheetHeight(getCurrentSheetHeight() + deltaHeight);
      event.preventDefault();
    };

    const hide = (evt?: Event) => {
      setSheetOpened(false);
      emit('update:modelValue', false);
      if (props.inline) return;
      dialog.value?.hide(evt);
    };

    const onDragEnd = (event?: PointerEvent) => {
      if (event && activePointerId !== null && activePointerId !== event.pointerId) return;

      if (event) {
        onDragMove(event);
      }

      if (event && activePointerId !== null) {
        (event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(activePointerId);
      }

      dragPosition = null;
      activePointerId = null;
      setDragMoving(false);

      const isSwipeDown = dragVelocity > BOTTOM_SHEET_SWIPE_CLOSE_VELOCITY;
      const isBelowCloseThreshold = getCurrentSheetHeight() <= getCloseThreshold();

      if (!props.persistent && event?.type !== 'pointercancel' && (isBelowCloseThreshold || isSwipeDown)) {
        setSheetHeight(initialSheetHeight);
        hide();
        return;
      }

      if (isBelowCloseThreshold) {
        setSheetHeight(initialSheetHeight);
        return;
      }

      if (!props.snap) {
        setSheetHeight(getCurrentSheetHeight());
        return;
      }

      if (getCurrentSheetHeight() > 75) {
        setSheetHeight(props.maxHeight);
        return;
      }

      if (getCurrentSheetHeight() > 45) {
        setSheetHeight(Math.min(60, props.maxHeight));
        return;
      }

      setSheetHeight(initialSheetHeight);
    };

    const show = (evt?: Event) => {
      setSheetOpened(true);
      prepareSheetHeight();

      if (props.inline) {
        emit('update:modelValue', true);
        void setInitialSheetHeight();
        return;
      }

      dialog.value?.show(evt);
    };
    const handleDialogShow = () => {
      setSheetOpened(true);
      void setInitialSheetHeight();
    };
    const handleDialogHide = () => {
      setSheetOpened(false);
    };
    const toggle = (evt?: Event) => {
      if (props.inline) {
        if (isInlineVisible.value) {
          hide(evt);
          return;
        }

        show(evt);
        return;
      }

      dialog.value?.toggle(evt);
    };
    const focus = (selector?: string) => {
      if (props.inline) {
        const focusTarget = selector ? getSheetElement()?.querySelector<HTMLElement>(selector) : getSheetElement();
        focusTarget?.focus();
        return;
      }

      dialog.value?.focus(selector);
    };
    const shake = (focusTarget?: Element) => {
      if (props.inline) return;
      dialog.value?.shake(focusTarget);
    };

    onMounted(() => {
      if (props.inline && isInlineVisible.value) {
        setSheetOpened(true);
        void setInitialSheetHeight();
      }

      window.addEventListener('resize', handleViewportResize);
      window.visualViewport?.addEventListener('resize', handleViewportResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleViewportResize);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);

      if (isSheetOpen) {
        setSheetOpened(false);
      }
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (props.inline) {
          setSheetOpened(value !== false);

          if (value !== false) {
            prepareSheetHeight();
            void setInitialSheetHeight();
          }
          return;
        }

        setSheetOpened(Boolean(value));

        if (value) {
          prepareSheetHeight();
        }
      },
      { flush: 'sync' }
    );

    watch(
      () => [props.initialHeight, props.fullOpening, props.maxHeight],
      () => {
        if (isSheetOpen && (props.fullOpening || props.initialHeight !== undefined)) {
          setSheetHeight(getInitialSheetHeight());
        }
      }
    );

    expose({
      show,
      hide,
      toggle,
      focus,
      shake,
      setHeight: setSheetHeight,
      handleDialogShow,
      handleDialogHide,
    });

    return {
      sheet,
      dialog,
      isInlineVisible,
      shouldShowHandle,
      sheetClasses,
      contentClasses,
      contentStyle,
      sheetStyle,
      onDragStart,
      onDragMove,
      onDragEnd,
      setInitialSheetHeight,
      handleDialogShow,
      handleDialogHide,
    };
  },
});
</script>

<template>
  <q-card
    v-if="inline && isInlineVisible"
    ref="sheet"
    v-bind="$attrs"
    class="bottom-sheet"
    :class="sheetClasses"
    :style="sheetStyle"
  >
    <div
      class="draggable-area"
      @pointerdown="onDragStart"
      @pointermove="onDragMove"
      @pointerup="onDragEnd"
      @pointercancel="onDragEnd"
    >
      <div v-if="shouldShowHandle" class="draggable-thumb"></div>
    </div>
    <div class="content" :class="contentClasses" :style="contentStyle">
      <slot />
    </div>
  </q-card>

  <teleport v-else-if="!inline" to="body">
    <q-dialog
      ref="dialog"
      position="bottom"
      :model-value="modelValue!"
      full-width
      :persistent="persistent"
      :seamless="seamless"
      transition-show="slide-up"
      transition-hide="slide-down"
      @update:model-value="$emit('update:modelValue', $event)"
      @show="handleDialogShow"
      @hide="handleDialogHide"
    >
      <q-card ref="sheet" v-bind="$attrs" class="bottom-sheet" :class="sheetClasses" :style="sheetStyle">
        <div
          class="draggable-area"
          @pointerdown="onDragStart"
          @pointermove="onDragMove"
          @pointerup="onDragEnd"
          @pointercancel="onDragEnd"
        >
          <div v-if="shouldShowHandle" class="draggable-thumb"></div>
        </div>
        <div class="content" :class="contentClasses" :style="contentStyle">
          <slot />
        </div>
      </q-card>
    </q-dialog>
  </teleport>
</template>

<style scoped lang="scss">
@import 'styles.module';
</style>
